import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChartBarIcon,
  CogIcon,
  DocumentTextIcon,
  FolderIcon,
  LockIcon,
  UsersIcon,
} from '../components/ui';
import { t } from '../i18n';

type IconComponent = ComponentType<{ className?: string }>;

type NavItem = {
  to: string;
  label: string;
  icon: IconComponent;
  end?: boolean;
};

function linkClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }): string =>
    [
      'group flex items-center rounded-lg text-sm transition-colors duration-150',
      collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2',
      isActive
        ? 'bg-teal-400/15 font-medium text-white ring-1 ring-teal-300/25'
        : 'text-slate-300 hover:bg-white/10 hover:text-white',
    ].join(' ');
}

function NavItemContent({
  label,
  icon: Icon,
  compact,
}: {
  label: string;
  icon: IconComponent;
  compact: boolean;
}) {
  return (
    <>
      <Icon className="h-4 w-4 shrink-0 opacity-90" />
      {!compact ? <span className="truncate">{label}</span> : null}
    </>
  );
}

type PlatformSidebarProps = {
  mobileOpen: boolean;
  collapsed: boolean;
  onNavigate: () => void;
  onToggleCollapse: () => void;
  onChangePassword: () => void;
};

export function PlatformSidebar({
  mobileOpen,
  collapsed,
  onNavigate,
  onToggleCollapse,
  onChangePassword,
}: PlatformSidebarProps) {
  const labels = t().nav;
  const platform = t().platform;
  const compact = collapsed && !mobileOpen;

  const navItems: NavItem[] = [
    {
      to: '/platform/tenants',
      label: platform.tabTenants,
      icon: FolderIcon,
      end: true,
    },
    {
      to: '/platform/packages',
      label: platform.tabPackages,
      icon: ChartBarIcon,
    },
    {
      to: '/platform/admins',
      label: platform.tabAdmins,
      icon: UsersIcon,
    },
    {
      to: '/platform/audit-logs',
      label: platform.tabAuditLogs,
      icon: DocumentTextIcon,
    },
    {
      to: '/platform/settings',
      label: platform.tabSettings,
      icon: CogIcon,
    },
  ];

  return (
    <aside
      className={[
        'fixed inset-y-0 left-0 z-40 flex h-full shrink-0 flex-col bg-sidebar text-white shadow-bf-md transition-[width,transform] duration-200 lg:static lg:translate-x-0 lg:shadow-none',
        compact ? 'w-[4.25rem]' : 'w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      ].join(' ')}
    >
      <div
        className={[
          'border-b border-white/10',
          compact ? 'px-2 py-4' : 'px-4 py-5',
        ].join(' ')}
      >
        {compact ? (
          <div
            className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-teal-400/15 text-sm font-semibold text-teal-200 ring-1 ring-teal-300/20"
            title={t().app.name}
          >
            BF
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-400/15 text-sm font-bold text-teal-200 ring-1 ring-teal-300/20">
              BF
            </div>
            <div className="min-w-0 pt-0.5">
              <div className="truncate font-display text-lg font-semibold tracking-tight">
                {t().app.name}
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-400">
                {platform.brandTag}
              </div>
            </div>
          </div>
        )}
      </div>

      <nav
        className={[
          'flex-1 space-y-1 overflow-y-auto scrollbar-thin',
          compact ? 'p-2' : 'p-3',
        ].join(' ')}
      >
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={linkClass(compact)}
            onClick={onNavigate}
            title={item.label}
          >
            <NavItemContent
              label={item.label}
              icon={item.icon}
              compact={compact}
            />
          </NavLink>
        ))}
      </nav>

      <div className={['border-t border-white/10', compact ? 'p-2' : 'p-3'].join(' ')}>
        <button
          type="button"
          onClick={() => {
            onChangePassword();
            onNavigate();
          }}
          className={[
            'flex w-full items-center rounded-lg text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white',
            compact ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2',
          ].join(' ')}
          title={platform.changePassword}
          aria-label={platform.changePassword}
        >
          <LockIcon className="h-4 w-4 shrink-0 opacity-90" />
          {!compact ? <span className="truncate">{platform.changePassword}</span> : null}
        </button>
      </div>

      <div className="hidden border-t border-white/10 p-2 lg:block">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={
            collapsed ? labels.expandSidebar : labels.collapseSidebar
          }
          title={collapsed ? labels.expandSidebar : labels.collapseSidebar}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeftIcon className="h-4 w-4" />
              <span>{labels.collapseSidebar}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
